import {
  ArrowRight,
  BarChart3,
  BookOpen,
  CheckCircle2,
  CircleDollarSign,
  Cloud,
  Filter,
  Gauge,
  Globe2,
  HelpCircle,
  Layers3,
  LifeBuoy,
  ListChecks,
  LockKeyhole,
  MousePointerClick,
  RadioTower,
  ShieldCheck,
  Workflow
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { LanguageMenu } from "./LanguageMenu";
import type { SupportedLocale } from "../lib/preferences";

type LandingPageProps = {
  locale: SupportedLocale;
  onLocaleChange: (locale: SupportedLocale) => Promise<void>;
  onSignIn: () => void;
};

type LandingIcon = ComponentType<SVGProps<SVGSVGElement>>;

const productCards: Array<{ key: string; icon: LandingIcon }> = [
  { key: "network", icon: Layers3 },
  { key: "cloud", icon: Cloud },
  { key: "tracker", icon: MousePointerClick },
  { key: "filter", icon: Filter },
  { key: "crm", icon: Workflow },
  { key: "postback", icon: RadioTower }
];

const playbookCards: Array<{ key: string; icon: LandingIcon }> = [
  { key: "fit", icon: CheckCircle2 },
  { key: "launch", icon: ListChecks },
  { key: "faq", icon: HelpCircle },
  { key: "support", icon: LifeBuoy },
  { key: "license", icon: CircleDollarSign },
  { key: "partnership", icon: Globe2 }
];

const audienceCards: Array<{ key: string; icon: LandingIcon }> = [
  { key: "network", icon: ShieldCheck },
  { key: "mediaBuying", icon: MousePointerClick },
  { key: "commerce", icon: BarChart3 }
];

const newsCards = ["release", "guide", "partnership"];
const footerColumns = ["platform", "tracker", "info", "extra"];

export function LandingPage({ locale, onLocaleChange, onSignIn }: LandingPageProps) {
  const { t } = useTranslation("common");
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className="landing-shell">
      <header className="landing-topbar">
        <div className="landing-brand">
          <span className="landing-brand-mark">LS</span>
          <span className="landing-brand-copy">
            <span>LeadStack</span>
            <strong>{t("app.scope")}</strong>
          </span>
        </div>

        <nav className="landing-nav" aria-label={t("landing.nav.label")}>
          <button onClick={() => scrollTo("landing-products")} type="button">
            {t("landing.nav.products")}
          </button>
          <button onClick={() => scrollTo("landing-playbook")} type="button">
            {t("landing.nav.services")}
          </button>
          <button onClick={() => scrollTo("landing-license")} type="button">
            {t("landing.nav.prices")}
          </button>
          <button onClick={() => scrollTo("landing-news")} type="button">
            {t("landing.nav.blog")}
          </button>
        </nav>

        <div className="landing-controls">
          <LanguageMenu onChange={onLocaleChange} value={locale} />
          <Button data-testid="landing-signin" icon={<ArrowRight aria-hidden="true" size={16} />} onClick={onSignIn} variant="primary">
            {t("landing.actions.signIn")}
          </Button>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-copy">
          <p className="eyebrow">{t("landing.badge")}</p>
          <h1>{t("landing.hero.title")}</h1>
          <p>{t("landing.hero.description")}</p>
          <div className="landing-hero-actions">
            <Button icon={<Gauge aria-hidden="true" size={16} />} onClick={() => scrollTo("landing-products")} variant="primary">
              {t("landing.actions.chooseProduct")}
            </Button>
            <Button
              icon={<BookOpen aria-hidden="true" size={16} />}
              onClick={() => scrollTo("landing-playbook")}
              variant="secondary"
            >
              {t("landing.actions.consultation")}
            </Button>
          </div>

          <div className="landing-proofline" aria-label={t("landing.proof.label")}>
            <span>{t("landing.proof.routing")}</span>
            <span>{t("landing.proof.offers")}</span>
            <span>{t("landing.proof.reports")}</span>
          </div>
        </div>

        <aside className="landing-console-preview" aria-label={t("landing.preview.label")}>
          <div className="preview-topline">
            <span />
            <span />
            <span />
            <strong>{t("landing.preview.title")}</strong>
          </div>
          <div className="preview-body">
            <section className="preview-map">
              <div>
                <strong>CRM</strong>
                <span>{t("landing.preview.latency")}</span>
              </div>
              <div>
                <strong>CPA</strong>
                <span>{t("landing.preview.redirect")}</span>
              </div>
              <div>
                <strong>API</strong>
                <span>{t("landing.preview.quality")}</span>
              </div>
            </section>

            <section className="preview-route">
              <p>{t("landing.preview.flow")}</p>
              <div className="preview-route-line">
                <span>{t("landing.preview.source")}</span>
                <ArrowRight aria-hidden="true" size={15} />
                <span>{t("landing.preview.rule")}</span>
                <ArrowRight aria-hidden="true" size={15} />
                <span>{t("landing.preview.offer")}</span>
              </div>
            </section>

            <section className="preview-table" aria-label={t("landing.preview.events")}>
              <div>
                <span>{t("landing.preview.eventLead")}</span>
                <strong>{t("landing.preview.statusOn")}</strong>
              </div>
              <div>
                <span>{t("landing.preview.eventPostback")}</span>
                <strong>{t("landing.preview.statusAuto")}</strong>
              </div>
              <div>
                <span>{t("landing.preview.eventPayout")}</span>
                <strong>{t("landing.preview.statusReady")}</strong>
              </div>
            </section>
          </div>
        </aside>
      </section>

      <section className="landing-band landing-products" id="landing-products">
        <div className="landing-section-head">
          <p className="eyebrow">{t("landing.products.badge")}</p>
          <h2>{t("landing.products.title")}</h2>
          <p>{t("landing.products.description")}</p>
        </div>

        <div className="landing-product-grid">
          {productCards.map((item) => {
            const Icon = item.icon;
            return (
              <article className="landing-product-card" key={item.key}>
                <Icon aria-hidden="true" />
                <span>{t(`landing.products.items.${item.key}.meta`)}</span>
                <h3>{t(`landing.products.items.${item.key}.title`)}</h3>
                <p>{t(`landing.products.items.${item.key}.description`)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="landing-band landing-playbook" id="landing-playbook">
        <div className="landing-section-head">
          <p className="eyebrow">{t("landing.playbook.badge")}</p>
          <h2>{t("landing.playbook.title")}</h2>
          <p>{t("landing.playbook.description")}</p>
        </div>

        <div className="landing-playbook-grid">
          {playbookCards.map((item) => {
            const Icon = item.icon;
            return (
              <article className="landing-playbook-card" key={item.key}>
                <Icon aria-hidden="true" />
                <h3>{t(`landing.playbook.items.${item.key}.title`)}</h3>
                <p>{t(`landing.playbook.items.${item.key}.description`)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="landing-band landing-audience">
        <div className="landing-section-head">
          <p className="eyebrow">{t("landing.audience.badge")}</p>
          <h2>{t("landing.audience.title")}</h2>
        </div>

        <div className="landing-audience-grid">
          {audienceCards.map((item) => {
            const Icon = item.icon;
            return (
              <article className="landing-audience-card" key={item.key}>
                <Icon aria-hidden="true" />
                <h3>{t(`landing.audience.items.${item.key}.title`)}</h3>
                <p>{t(`landing.audience.items.${item.key}.description`)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="landing-band landing-license" id="landing-license">
        <div className="landing-section-head">
          <p className="eyebrow">{t("landing.license.badge")}</p>
          <h2>{t("landing.license.title")}</h2>
          <p>{t("landing.license.description")}</p>
        </div>

        <div className="landing-license-grid">
          <article className="landing-license-card">
            <LockKeyhole aria-hidden="true" />
            <h3>{t("landing.license.items.private.title")}</h3>
            <p>{t("landing.license.items.private.description")}</p>
          </article>
          <article className="landing-license-card landing-license-card-accent">
            <Cloud aria-hidden="true" />
            <h3>{t("landing.license.items.cloud.title")}</h3>
            <p>{t("landing.license.items.cloud.description")}</p>
          </article>
          <article className="landing-license-card">
            <LifeBuoy aria-hidden="true" />
            <h3>{t("landing.license.items.support.title")}</h3>
            <p>{t("landing.license.items.support.description")}</p>
          </article>
        </div>
      </section>

      <section className="landing-band landing-news" id="landing-news">
        <div className="landing-section-head">
          <p className="eyebrow">{t("landing.news.badge")}</p>
          <h2>{t("landing.news.title")}</h2>
          <p>{t("landing.news.description")}</p>
        </div>

        <div className="landing-news-grid">
          {newsCards.map((item) => (
            <article className="landing-news-card" key={item}>
              <time>{t(`landing.news.items.${item}.date`)}</time>
              <h3>{t(`landing.news.items.${item}.title`)}</h3>
              <p>{t(`landing.news.items.${item}.description`)}</p>
            </article>
          ))}
        </div>
      </section>

      <footer className="landing-footer">
        <div>
          <strong>LeadStack</strong>
          <p>{t("landing.footer.note")}</p>
        </div>
        <nav className="landing-footer-columns" aria-label={t("landing.footer.label")}>
          {footerColumns.map((column) => (
            <section key={column}>
              <h3>{t(`landing.footer.columns.${column}.title`)}</h3>
              <a href="#landing-products">{t(`landing.footer.columns.${column}.item1`)}</a>
              <a href="#landing-playbook">{t(`landing.footer.columns.${column}.item2`)}</a>
              <a href="#landing-news">{t(`landing.footer.columns.${column}.item3`)}</a>
            </section>
          ))}
        </nav>
        <Button data-testid="landing-footer-signin" icon={<ArrowRight aria-hidden="true" size={16} />} onClick={onSignIn} variant="secondary">
          {t("landing.actions.signIn")}
        </Button>
      </footer>
    </main>
  );
}
